/**
 * 评估器判定失败时刻「前 3 秒、后 2 秒」窗口图：两侧自车速度 + 两侧位置差，竖线标出失败时刻。
 *
 * 数据来自管线（失败事件话题的纳秒时间 + 两侧自车序列）。窗口落在数据范围之外的部分是空的
 * （真实数据里出现过：失败时刻在场景数据末尾之后），图上留空而不是外推，并在标题旁提示。
 */
import { Tag } from 'antd';
import ReactECharts from 'echarts-for-react';
import { useMemo } from 'react';

import {
  endedAfter,
  failWindowCoverage,
  failWindowPoints,
  failWindowTitle,
  type FailWindowLike,
} from '../format';

export default function FailWindowChart({ w }: { w: FailWindowLike }) {
  const { have, total } = failWindowCoverage(w);
  const curEnd = endedAfter(w, w.cur?.v ?? []);
  const baseEnd = endedAfter(w, w.base?.v ?? []);
  const option = useMemo(() => {
    const pts = failWindowPoints(w);
    const x = pts.map((p) => p.t.toFixed(1));
    const zero = x.indexOf('0.0');
    const endLine = (end: number | null, name: string, color: string) =>
      end == null ? [] : [{ xAxis: end.toFixed(1), lineStyle: { color, type: 'solid' as const }, label: { formatter: `${name}已终止`, color, position: 'insideEndTop' as const } }];
    return {
      grid: { left: 48, right: 48, top: 34, bottom: 28 },
      tooltip: { trigger: 'axis' },
      legend: { top: 0, data: ['当前速度', '基线速度', '位置差'] },
      xAxis: { type: 'category', data: x, name: '相对失败时刻（秒）', nameLocation: 'middle', nameGap: 22 },
      yAxis: [
        { type: 'value', name: '速度 m/s', scale: true },
        { type: 'value', name: '位置差 m', scale: true, splitLine: { show: false } },
      ],
      series: [
        {
          name: '当前速度', type: 'line', showSymbol: false, data: pts.map((p) => p.curV), connectNulls: false,
          lineStyle: { color: '#5e6ad2' }, itemStyle: { color: '#5e6ad2' },
          markLine: (zero >= 0 || curEnd != null || baseEnd != null) ? {
            symbol: 'none', silent: true, lineStyle: { color: '#dc2626', type: 'dashed' },
            label: { formatter: '失败', color: '#dc2626' },
            data: [...(zero >= 0 ? [{ xAxis: zero }] : []), ...endLine(curEnd, '当前', '#5e6ad2'), ...endLine(baseEnd, '基线', '#e8893a')],
          } : undefined,
        },
        { name: '基线速度', type: 'line', showSymbol: false, data: pts.map((p) => p.baseV), connectNulls: false,
          lineStyle: { color: '#e8893a' }, itemStyle: { color: '#e8893a' } },
        { name: '位置差', type: 'line', yAxisIndex: 1, showSymbol: false, data: pts.map((p) => p.dPos), connectNulls: false,
          lineStyle: { color: '#6b7280', type: 'dotted' }, itemStyle: { color: '#6b7280' } },
      ],
    };
  }, [w]);

  return (
    <div className="bc-failwin">
      <div className="bc-failwin-title">
        {failWindowTitle(w)}
        {curEnd != null || baseEnd != null ? (
          <Tag color="processing" bordered={false} style={{ marginLeft: 8 }}>
            {[curEnd != null ? `当前侧在 ${curEnd.toFixed(1)} 秒处已终止` : null,
              baseEnd != null ? `基线侧在 ${baseEnd.toFixed(1)} 秒处已终止` : null].filter(Boolean).join('；')}
          </Tag>
        ) : null}
        {have < total && curEnd == null && baseEnd == null ? (
          <Tag color="warning" bordered={false} style={{ marginLeft: 8 }}>
            窗口内只有 {have}/{total} 个点有数据（失败时刻靠近数据边缘）
          </Tag>
        ) : null}
      </div>
      {w.msg ? <div className="bc-failwin-msg" title={w.msg}>{w.msg}</div> : null}
      <ReactECharts option={option} style={{ height: 220 }} notMerge />
    </div>
  );
}
